import { Injectable, inject } from '@angular/core';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';

import { LinkAnchorData } from './operations.service';
import { PortsService } from './ports.service';
import { PositionService } from './position.service';
import { ElementClasses } from '../../model/element-classes.model';
import { FlowchartConfig, FontAttributes } from '../base/flowchart.config';

export type CreateDecisionNodeOptions = {
  x: number;
  y: number;
  tooltip: string;
  id: string;
  uniqueId: string;
  size: { width: number; height: number };
  colorPresetId: string;
};

@Injectable({
  providedIn: 'root',
})
export class DecisionNodeService {
  private positionService = inject(PositionService);
  private translocoService = inject(TranslocoService);

  createDecision(text: string, options?: Partial<CreateDecisionNodeOptions>): joint.dia.Cell {
    const nodeText = text ?? '';

    const newDecision = new joint.shapes.standard.Rectangle({
      position: { x: options?.x, y: options?.y },
      size: options?.size ? options?.size : FlowchartConfig.nodeSize.default,
      z: 1,
      attrs: {
        root: {
          'data-tooltip': options?.tooltip,
          'data-flowchart-element-type': `decision`,
        },
        body: {
          class: `element--decision__body ${ElementClasses.ElementBody}`,
          rx: FlowchartConfig.actionNode.styles.borderRadius,
          ry: FlowchartConfig.actionNode.styles.borderRadius,
          uniqueId: options?.uniqueId ? options?.uniqueId : '',
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-fill) !important` : undefined,
          stroke: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-stroke) !important` : undefined,
          'data-testid': `flowchart-element_body`,
        },
        label: {
          ...FontAttributes,
          class: `element--decision__text ${ElementClasses.ElementText}`,
          'data-testid': `flowchart-element_label`,
          fontSize: FontAttributes.fontSize,
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-text) !important` : undefined,
          text: nodeText,
          textWrap: {
            width: -10, // reference width minus 10
            ellipsis: true,
          },
          textVerticalAnchor: 'middle',
        },
      },
    });

    if (options?.id) {
      newDecision.set('id', options?.id);
    }

    newDecision.attr('body/unique-id', options?.uniqueId ?? newDecision.id);

    PortsService.addPorts(newDecision);
    return newDecision;
  }

  createDecisionInDefaultPosition(
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    overwrittenPosition?: joint.g.PlainPoint,
  ): joint.dia.Cell {
    const title = this.translocoService.translate(FlowchartConfig.defaultTitleKey);

    if (overwrittenPosition) {
      return this.createDecision(title, { x: overwrittenPosition.x, y: overwrittenPosition.y, tooltip: title });
    }

    const decisionPosition = this.positionService.calculateStepPosition(
      sourceElement.model.attributes.size,
      sourceElement.model.attributes.position,
      sourceAnchor,
      FlowchartConfig.nodeSize.default,
    );

    return this.createDecision(title, { x: decisionPosition.x, y: decisionPosition.y, tooltip: title });
  }
}
